import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { SubscriptionPackage, SubscriptionService } from '../../../core/services';

export interface PackageSaveEvent {
  mode: 'create' | 'edit';
  refId?: number;
}

@Component({
  selector: 'app-add-edit-package',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './add-edit-package.component.html',
  styleUrls: ['./add-edit-package.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class AddEditPackageComponent implements OnInit {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialPackage: SubscriptionPackage | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<PackageSaveEvent>();

  packageForm: FormGroup;
  isSubmitting = false;
  error: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly subscriptionService: SubscriptionService
  ) {
    this.packageForm = this.fb.group({
      packageName: ['', Validators.required],
      totalFlat: [0, [Validators.required, Validators.min(0)]],
      totalRenter: [0, [Validators.required, Validators.min(0)]],
      maxActiveEmployee: [0, [Validators.required, Validators.min(0)]],
      maxFileMemory: [0, [Validators.required, Validators.min(0)]],
      priceMonthly: [0, [Validators.required, Validators.min(0)]],
      priceYearly: [0, [Validators.required, Validators.min(0)]],
      status: [1]
    });
  }

  ngOnInit(): void {
    if (this.mode !== 'edit' || !this.initialPackage) {
      return;
    }

    this.packageForm.patchValue({
      packageName: this.initialPackage.packageName ?? '',
      totalFlat: this.initialPackage.totalFlat ?? 0,
      totalRenter: this.initialPackage.totalRenter ?? 0,
      maxActiveEmployee: this.initialPackage.maxActiveEmployee ?? 0,
      maxFileMemory: this.initialPackage.maxFileMemory ?? 0,
      priceMonthly: this.initialPackage.priceMonthly ?? 0,
      priceYearly: this.initialPackage.priceYearly ?? 0,
      status: 1
    });
  }

  closeModal(): void {
    if (!this.isSubmitting) {
      this.close.emit();
    }
  }

  hasError(controlName: string, errorKey?: string): boolean {
    const control = this.packageForm.get(controlName);
    if (!control || !control.touched) {
      return false;
    }
    return errorKey ? control.hasError(errorKey) : control.invalid;
  }

  clearZeroOnFocus(controlName: string): void {
    const control = this.packageForm.get(controlName);
    if (!control) {
      return;
    }

    const value = Number(control.value ?? 0);
    if (Number.isFinite(value) && value === 0) {
      control.setValue('', { emitEvent: false });
    }
  }

  onSubmit(): void {
    if (this.packageForm.invalid) {
      this.packageForm.markAllAsTouched();
      this.error = 'Please fix the highlighted fields.';
      return;
    }

    const raw = this.packageForm.getRawValue();
    const payload = {
      id: this.mode === 'edit' ? Number(this.initialPackage?.id) : undefined,
      packageName: `${raw.packageName ?? ''}`.trim(),
      totalFlat: Number(raw.totalFlat ?? 0),
      totalRenter: Number(raw.totalRenter ?? 0),
      maxActiveEmployee: Number(raw.maxActiveEmployee ?? 0),
      maxFileMemory: Number(raw.maxFileMemory ?? 0),
      priceMonthly: Number(raw.priceMonthly ?? 0),
      priceYearly: Number(raw.priceYearly ?? 0),
      status: this.mode === 'edit' ? 1 : Number(raw.status ?? 1)
    };

    this.isSubmitting = true;
    this.error = null;

    const request$ = this.mode === 'edit'
      ? this.subscriptionService.updatePackage(payload)
      : this.subscriptionService.createPackage(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res: any) => {
          if (`${res?.code ?? ''}` !== '000') {
            this.error = `${res?.message ?? `Failed to ${this.mode === 'edit' ? 'update' : 'create'} package.`}`;
            return;
          }

          const refId = Number(res?.refId ?? this.initialPackage?.id);
          this.save.emit({
            mode: this.mode,
            refId: Number.isFinite(refId) ? refId : undefined
          });
        },
        error: (err: any) => {
          this.error = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} package.`;
        }
      });
  }
}
